import React, { useState } from 'react';
import { Database, Download, CheckCircle, AlertTriangle, Loader2, Info, HardDrive, FileArchive, Clock } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { useCreerBackupMutation, useListerBackupsQuery } from '../../services/coreApi';
import type { BackupResponse } from '../../services/coreApi';

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
};

const SauvegardesTab: React.FC = () => {
  const [creerBackup, { isLoading }] = useCreerBackupMutation();
  const { data: backupsData, isLoading: backupsLoading, refetch: refetchBackups } = useListerBackupsQuery();
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastBackup, setLastBackup] = useState<BackupResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleBackup = async () => {
    setShowConfirm(false);
    setError(null);
    try {
      const result = await creerBackup().unwrap();
      setLastBackup(result);
      refetchBackups();
    } catch (err: any) {
      setError(err.data?.error || err.data?.detail || 'Erreur lors de la création du backup');
    }
  };

  return (
    <div className="space-y-6">
      {/* Backup creation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Database size={24} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Créer une sauvegarde</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Crée une copie complète de la base de données PostgreSQL (pg_dump).
              La sauvegarde est stockée sur le serveur.
            </p>
            <div className="mt-4">
              <Button
                variant="primary"
                icon={isLoading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                onClick={() => setShowConfirm(true)}
                disabled={isLoading}
              >
                {isLoading ? 'Backup en cours...' : 'Créer un backup'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Result */}
      {lastBackup && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-600 dark:text-green-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-green-800 dark:text-green-300">Backup créé avec succès</h4>
              <div className="mt-2 space-y-1 text-sm text-green-700 dark:text-green-400">
                <p><strong>Fichier :</strong> {lastBackup.backup_filename}</p>
                <p><strong>Taille :</strong> {formatSize(lastBackup.taille)}</p>
                <p><strong>Emplacement :</strong> <code className="px-1 py-0.5 bg-green-100 dark:bg-green-800/30 rounded text-xs">{lastBackup.backup_path}</code></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800 dark:text-red-300">Erreur</h4>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-blue-500 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-300">Informations sur les sauvegardes</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-400 mt-2 space-y-1 list-disc list-inside">
              <li>Les backups sont stockés sur le serveur dans le dossier <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800/30 rounded text-xs">backend/backups/</code></li>
              <li>Format : pg_dump avec compression niveau 9</li>
              <li>Chaque backup est horodaté automatiquement</li>
              <li>Une sauvegarde automatique est créée avant chaque suppression de données</li>
              <li>Pour restaurer un backup, utilisez la commande <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800/30 rounded text-xs">pg_restore</code> depuis le serveur</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Liste des backups */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardDrive size={18} className="text-gray-500 dark:text-gray-400" />
            <h4 className="font-medium text-gray-900 dark:text-gray-100">Backups existants</h4>
            {backupsData && <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{backupsData.total}</span>}
          </div>
        </div>
        {backupsLoading ? (
          <div className="flex justify-center py-8"><Spinner size="sm" /></div>
        ) : backupsData && backupsData.backups.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-64 overflow-y-auto">
            {backupsData.backups.map((b, i) => (
              <div key={b.filename} className={`flex items-center justify-between px-5 py-3 ${i % 2 ? 'bg-gray-50 dark:bg-gray-800/50' : ''}`}>
                <div className="flex items-center gap-3">
                  <FileArchive size={16} className="text-blue-500 shrink-0" />
                  <div>
                    <p className="text-sm font-mono font-medium text-gray-800 dark:text-gray-200">{b.filename}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-2">
                      <Clock size={10} />
                      {new Date(b.date_creation).toLocaleString('fr-FR')}
                    </p>
                  </div>
                </div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{formatSize(b.taille)}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center py-6 text-sm text-gray-400">Aucun backup trouvé</p>
        )}
        {backupsData?.repertoire && (
          <div className="px-5 py-2 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-400">
            Répertoire : <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">{backupsData.repertoire}</code>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database size={20} className="text-blue-500" />
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Créer un backup ?</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Cette opération va créer une copie complète de la base de données. Elle peut prendre quelques instants.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Annuler</Button>
              <Button variant="primary" onClick={handleBackup}>Confirmer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SauvegardesTab;
